import React, {useState, useMemo} from 'react'
import {Modal, Button, Spinner} from 'react-bootstrap'
import {
  useGetFlowPausedQuery,
  useResumeFlowPausedMutation,
} from '../../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import clsx from 'clsx'
import styles from '../../../../../Components/DeleteConfirmationPopup.module.scss'
import {useNavigate} from 'react-router-dom'
import PurchasePopup, {
  getMinimumCredit,
  MINIMUM_CREDIT,
} from '../../../../../Components/PurchasePopup'

interface ResumeFlowModalProps {
  show: boolean
  onHide: () => void
  flowId: string
  flowName: string
  onSuccess?: () => void
}

const ResumeFlowModal: React.FC<ResumeFlowModalProps> = ({
  show,
  onHide,
  flowId,
  flowName,
  onSuccess,
}) => {
  const [isResuming, setIsResuming] = useState(false)
  const navigate = useNavigate()
  const [purchasePopupModal, setPurchasePopupModal] = useState(false)
  const [purchaseModelData, setPurchaseModelData] = useState({
    type: 'sms',
    purchaseCredit: 0,
    minimum: 0,
    purpose: 'resume the flow',
  })
  const {
    refetch: refetchPausedData,
    data: pausedData,
    isLoading: isPausedLoading,
    error: pausedError,
  } = useGetFlowPausedQuery({id: flowId}, {skip: !show || !flowId})

  const [resumeFlowPaused] = useResumeFlowPausedMutation()

  const handleResume = async () => {
    setIsResuming(true)
    try {
      const response = await resumeFlowPaused({id: flowId}).unwrap()

      if (response.status) {
        toast.success(response.message || 'Flow resumed successfully!')
        // refresh all data
        refetchPausedData()
        onSuccess?.()
        onHide()
      } else {
        toast.error(response.message || 'Failed to resume flow')
      }
    } catch (error: any) {
      if (error?.status === 400) {
        const errorMessage = error?.data?.message || 'Failed to resume flow'

        // Check if the error message indicates insufficient credits
        if (
          errorMessage.toLowerCase().includes('credit') ||
          errorMessage.toLowerCase().includes('insufficient')
        ) {
          // Extract credit amount from error message if available
          const creditMatch = errorMessage.match(/(\d+)\s*credit/i)
          const requiredCredits = creditMatch ? parseInt(creditMatch[1]) : MINIMUM_CREDIT

          setPurchaseModelData({
            type: 'sms',
            purchaseCredit: getMinimumCredit(requiredCredits),
            minimum: requiredCredits,
            purpose: 'resume the flow (SMS required)',
          })
          setPurchasePopupModal(true)
        } else {
          toast.error(errorMessage)
        }
      } else {
        toast.error('An unexpected error occurred while resuming the flow')
      }
    } finally {
      setIsResuming(false)
    }
  }

  const pausedCount = pausedData?.data?.total || 0
  const hasPausedMessages = pausedCount > 0

  const purchaseModal = useMemo(
    () => (
      <PurchasePopup
        show={purchasePopupModal}
        purchaseModelData={purchaseModelData}
        handleClose={() => {
          setPurchasePopupModal(false)
        }}
        purpose={purchaseModelData.purpose}
      />
    ),
    [purchasePopupModal, purchaseModelData]
  )

  return (
    <>
      {purchaseModal}
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName={clsx('modal-dialog-centered mw-600px', {
          'resume-flow-modal': true,
        })}
      >
        <div className={clsx(styles.iconBox, 'col-12 pt-4')}>
          <i
            className={clsx('fa fa-exclamation', {
              [styles.redCircle]: true,
            })}
          ></i>
        </div>

        <Modal.Body className={clsx(styles.modalBody)}>
          {isPausedLoading ? (
            <div className='text-center py-4'>
              <Spinner animation='border' />
              <p className='mt-2 text-muted'>Loading flow information...</p>
            </div>
          ) : pausedError ? (
            <div className='text-center py-4'>
              <i className='bi bi-exclamation-circle text-danger fs-1'></i>
              <p className='mt-2 text-danger'>Failed to load flow information</p>
            </div>
          ) : (
            <>
              <div className='text-center fs-3 mb-2'>
                <strong>Reactivation Warning</strong>
              </div>

              <div className='text-center mb-4'>
                <h5 className='text-dark mb-3'>Flow: {flowName}</h5>

                {hasPausedMessages ? (
                  <div className='mb-4'>
                    <p className='mb-2'>
                      There are <strong>{pausedCount}</strong> unsent messages queued in this flow.
                    </p>
                    <p className='mb-0'>
                      These will be sent immediately upon reactivation. Please review the{' '}
                      <button
                        type='button'
                        className='btn btn-link p-0  fw-bold'
                        onClick={() => {
                          navigate('/automation/contacts-in-flows/' + flowId)
                        }}
                      >
                        "Contact in Flow" page
                      </button>{' '}
                      or contact your account manager if you're unsure or need assistance.
                    </p>
                  </div>
                ) : (
                  <div className='alert alert-success border-0 mb-4'>
                    <p className='mb-0'>
                      <i className='bi bi-check-circle me-2'></i>
                      No paused messages found. The flow doesn’t need reactivation.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className={clsx(styles.modalFooter, 'd-flex justify-content-center gap-3')}>
          <Button
            className={clsx(styles.deleteCancelBtn)}
            style={{background: 'transparent', color: 'black'}}
            onClick={onHide}
            disabled={isResuming}
          >
            No, Cancel
          </Button>
          {hasPausedMessages && (
            <Button
              className={clsx(styles.deleteSuccessBtn, {
                [styles.btnGreen]: true,
              })}
              disabled={isResuming || isPausedLoading}
              onClick={handleResume}
              style={{backgroundColor: '#28a745', borderColor: '#28a745'}}
            >
              {isResuming ? (
                <>
                  <Spinner size='sm' className='me-2' />
                  Reactivating...
                </>
              ) : (
                'Yes, Reactivate!'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ResumeFlowModal
